<template>
  <div :class="['relative -mx-6', { collapsed }]">
    <div ref="eventCalendarRef"></div>
    <SafeTeleport :key="collapsed" :to="`#portal-target-resource-header`">
      <div class="flex justify-between items-center h-3">
        <div v-if="!collapsed" class="font-medium text-gray-900 ml-4">
          Technicians
        </div>
        <div
          :class="['cursor-pointer ml-1', { 'rotate-180': collapsed }]"
          @click="toggleCollapsed"
        >
          <CollapseLogo class="w-4 text-gray-400" />
        </div>
      </div>
    </SafeTeleport>
    <UnassignedEventsSlideout v-if="unassignedSlideoutEnabled" />
    <TimelineFooter :last-load="lastLoad" :collapsed="collapsed" />
    <AppointmentDetailsDrawer
      v-if="selectedAppointmentId"
      :appointment-id="selectedAppointmentId"
      :placement="placement"
      @hide-drawer="onHideDrawer"
      @appointment-updated="onAppointmentUpdated"
    />
    <AppointmentCreateModal
      v-if="createSlot && isSheet"
      sheet
      :initial_date="createSlot.date"
      :scheduled_time="createSlot.scheduledTime"
      :technician_id="createSlot.technicianUserId"
      @hide-modal="clearCreateSlot"
      @created="onCreated"
    />
    <!-- Held back until the flag has actually answered: the two surfaces differ
         by more than a class (the desktop one is the jQuery modal, which shows
         itself and registers a backdrop on mount), so mounting the wrong one
         first and swapping would strand that backdrop. -->
    <CalendarEventModal
      v-if="selectedEventId && flagsResolved"
      :calendar-event-id="selectedEventId"
      :sheet="isSheet"
      auto-open
      @close="closeEventModal"
    ></CalendarEventModal>
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import moment from "moment-timezone";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { getComponentHtml } from "@/lib/getComponentHTML";
import { getObjDiff, initTippyTooltips } from "@/lib/helpers";
import { convertToTenantTimezone } from "@/lib/helpers";
import { getQueryVariables } from "@/lib/url";

import AppointmentCreateModal from "@/AppointmentCreateModal/AppointmentCreateModal.vue";
import AppointmentDetailsDrawer from "@/AppointmentDetails/AppointmentDetailsDrawer.vue";
import { useSheetPlacement } from "@/composables/useSheetPlacement";
import CollapseLogo from "@/images/log-in.svg?component";
import CalendarEventModal from "@/Scheduler/CalendarEventModal.vue";
import { useEventCalendar } from "@/SchedulerV2/useEventCalendar";
import { AdminPreference } from "@/typings/adminPreferences";
import { AppointmentV2 } from "@/typings/appointment";
import {
  MbscCalendarEventData,
  MbscCellClickEvent,
  MbscEventClickEvent,
  MbscEventCreateEvent,
  MbscEventUpdatedEvent,
  MbscResource
} from "@mobiscroll/javascript";
import AppointmentEvent from "./AppointmentEvent.vue";
import ResourceContent from "./ResourceContent.vue";
import TimelineFooter from "./TimelineFooter.vue";
import UnassignedEventsSlideout from "./UnassignedEventsSlideout.vue";
import UnassignedResourceCell from "./UnassignedResourceCell.vue";

const {
  applyDragGate,
  clearCreateSlot,
  dragOptionsFor,
  createSlot,
  handleCellClick,
  initCalendar,
  destroyCalendar,
  setCalendarDate,
  refreshTimeline,
  reRenderResources,
  unassignedEvents,
  selectedAppointmentId,
  selectedEventId,
  setTimezoneLabel,
  handleEventRightClick,
  handleEventClick
} = useEventCalendar();
// This is the DESKTOP timeline, but SchedulePageV2 only swaps to MobileTimeline
// below 640px while the sheet gate is the md breakpoint (853px). Tablet-portrait
// widths therefore render this component with a bottom sheet, which is
// deliberate -- it is the band where the sheet is wanted and MobileTimeline
// does not reach.
const { isSheet, placement, flagsResolved } = useSheetPlacement();
const store = useStore();
const props = defineProps<{ date: Date }>();
const emit = defineEmits<{
  (e: "date-navigated", date: Date | string): void;
}>();
const eventCalendarRef = ref<HTMLElement | null>(null);
const lastLoad = ref(new Date());
const collapsed = ref(false);

const unassignedSlideoutEnabled = computed(() => {
  return store.state.adminPreferences.find(
    (pref: AdminPreference) =>
      pref.key === "sheduler_unassigned_events_floating"
  )?.enabled;
});

onMounted(() => {
  initCalendar(eventCalendarRef.value, props.date, {
    height: getHeight(),
    renderResource: (data: MbscResource) => {
      if (data.isParent) {
        return !collapsed.value ? data.name : "";
      }
      if (data.id === -1) {
        return getComponentHtml(UnassignedResourceCell, {
          resource: data,
          collapsed: collapsed.value,
          unassignedEvents: unassignedEvents.value
        });
      }
      return getComponentHtml(ResourceContent, {
        resource: data,
        collapsed: collapsed.value
      });
    },
    renderResourceHeader,
    renderScheduleEvent: (data: MbscCalendarEventData) => {
      setTimeout(() => initTippyTooltips());
      return getComponentHtml(AppointmentEvent, {
        event: data
      });
    },
    onPageLoaded: handlePageLoad,
    onEventClick: handleEventClick,
    onEventHoverIn: handleEventHoverIn,
    onEventHoverOut: handleEventHoverOut,
    onEventUpdated: handleEventUpdated,
    onEventCreate: handleEventCreate,
    onEventRightClick: handleEventRightClick,
    onCellClick: onCellClick,
    ...dragOptionsFor(isSheet.value)
  });
  // Auto-open appointment details drawer if appointment_id query param is present
  const params = getQueryVariables();
  if (params.appointment_id) {
    selectedAppointmentId.value = params.appointment_id;
  }

  window.addEventListener(
    "resize",
    debounce(() => {
      refreshTimeline();
    }, 1000)
  );
});

onBeforeUnmount(() => {
  destroyCalendar();
});

watch(
  () => props.date,
  (newVal) => {
    debouncedDateChange(newVal);
  }
);

watch(collapsed, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    reRenderResources();
  }
});

watch(isSheet, (sheet) => applyDragGate(sheet));

const debouncedDateChange = debounce(
  (date: Date) => setCalendarDate(date),
  750
);
const closeEventModal = () => {
  selectedEventId.value = "";
};
const getHeight = () => {
  if (!eventCalendarRef.value) return;
  const bodyTopOffset = eventCalendarRef.value.getBoundingClientRect().top;
  const FOOTER_OFFSET = 100;
  return window.innerHeight - bodyTopOffset - FOOTER_OFFSET;
};
const renderResourceHeader = (): string => {
  return `<div id="portal-target-resource-header"></div>`;
};
const toggleCollapsed = () => {
  collapsed.value = !collapsed.value;
};
const handleEventHoverIn = (args: MbscEventClickEvent) => {
  $(args.domEvent.target).addClass("timeline-event-hovered");
  $(args.domEvent.target).removeAttr("title");
};
const handleEventHoverOut = (args: MbscEventClickEvent) => {
  $(args.domEvent.target).removeClass("timeline-event-hovered");
};
const handlePageLoad = () => {
  setTimeout(() => (lastLoad.value = new Date()), 100);
  initTippyTooltips();
  setClickListeners();
  setTimezoneLabel();
};
const setClickListeners = () => {
  document
    .querySelector("#unassignedRowToggleButton")
    ?.addEventListener("click", toggleUnassignedPreference);
};
const toggleUnassignedPreference = () => {
  store.dispatch("enablePreference", "sheduler_unassigned_events_floating");
  // refresh schedule board
  window.location.reload();
};
const handleEventUpdated = async (args: MbscEventUpdatedEvent) => {
  if (!args.event.id) return;
  const changes = getObjDiff(args.oldEvent, args.event);
  if (!Object.keys(changes).length) return;
  let payload: any = {
    skip_notifications: true,
    appointmentId: args.event.id
  };
  if (changes.resource) {
    // Handle resource change
    const technician_profile_id =
      changes.resource === -1 ? null : changes.resource;
    payload = {
      ...payload,
      technician_profile_id
    };
  }
  if (
    changes.start &&
    moment(changes.start).format() !== moment(args.oldEvent.start).format()
  ) {
    // Handle reschedule
    payload = {
      ...payload,
      arrival_window_start: convertToTenantTimezone(args.event.start).format(
        "YYYY-MM-DD[T]HH:mm:ssZ"
      ),
      arrival_window_end: convertToTenantTimezone(args.event.end).format(
        "YYYY-MM-DD[T]HH:mm:ssZ"
      ),
      scheduled_time: moment(args.event.start)
        .add(1, "hour")
        .format("YYYY-MM-DD[T]HH:mm:ssZ")
    };
  } else if (changes.end) {
    // Handle duration change if only end time has changed
    const duration = moment
      .duration(moment(args.event.end).diff(moment(args.event.start)))
      .asMinutes();
    payload = {
      ...payload,
      duration_minutes: duration
    };
  }
  await store.dispatch("updateAppointmentEvent", payload);
  window.notify.success("Appointment updated");
  refreshTimeline();
};
const handleEventCreate = (args: MbscEventCreateEvent) => {
  if (args.event.resource && args.event.id) {
    store.dispatch("assignAppointmentEvent", {
      technician_profile_id: args.event.resource,
      appointmentId: args.event.id
    });
    refreshTimeline();
  }
};
const onHideDrawer = () => (selectedAppointmentId.value = null);

// Guarded here rather than in the composable: placement belongs to the call
// site. At desktop widths drag-to-create is still how an appointment is made,
// so a tap has to stay inert.
const onCellClick = (args: MbscCellClickEvent) => {
  if (!isSheet.value) return;
  handleCellClick(args);
};

// No refresh here: handleSave emits refresh-schedule-board and the composable
// listens for it, so calling refreshTimeline would fetch the board twice.
const onCreated = () => {
  clearCreateSlot();
};
const onAppointmentUpdated = (appointment: AppointmentV2) => {
  // Route drawer-driven navigation through the page's selected date so the
  // header, the modal default, and the URL all follow the calendar
  emit("date-navigated", appointment.arrival_window_start);
};
</script>

<style lang="scss">
.mbsc-timeline-header-date,
.mbsc-calendar-controls {
  display: none;
}
.mbsc-timeline-resource {
  padding-left: 0 !important;
}
.mbsc-timeline-resource-col {
  width: 219px !important;
  transition: width ease 150ms;
}
.collapsed {
  .mbsc-timeline-resource-col {
    width: 58px !important;
  }
}
.mbsc-timeline-resource-bg {
  position: relative;
  &::before {
    content: " ";
    position: absolute;
    height: 100%;
    width: 27px;
    right: -27px;
    pointer-events: none;
    top: 0px;
    opacity: 0.4;
    background: linear-gradient(
      90deg,
      rgba(148, 154, 168, 0.3) -10.34%,
      rgba(234, 238, 245, 0) 81.03%
    );
  }
}
.mbsc-ios.mbsc-schedule-all-day-item:after,
.mbsc-ios.mbsc-schedule-column,
.mbsc-ios.mbsc-schedule-item,
.mbsc-ios.mbsc-schedule-resource,
.mbsc-ios.mbsc-schedule-resource-group,
.mbsc-ios.mbsc-timeline-column,
.mbsc-ios.mbsc-timeline-day:after,
.mbsc-ios.mbsc-timeline-footer,
.mbsc-ios.mbsc-timeline-footer-column,
.mbsc-ios.mbsc-timeline-footer-date,
.mbsc-ios.mbsc-timeline-footer-week,
.mbsc-ios.mbsc-timeline-header,
.mbsc-ios.mbsc-timeline-header-column,
.mbsc-ios.mbsc-timeline-header-date,
.mbsc-ios.mbsc-timeline-header-month,
.mbsc-ios.mbsc-timeline-header-week,
.mbsc-ios.mbsc-timeline-resource,
.mbsc-ios.mbsc-timeline-resource-footer-cont,
.mbsc-ios.mbsc-timeline-resource-header-cont,
.mbsc-ios.mbsc-timeline-row,
.mbsc-ios.mbsc-timeline-row-date,
.mbsc-ios.mbsc-timeline-row-group:after,
.mbsc-ios.mbsc-timeline-sidebar-footer-cont,
.mbsc-ios.mbsc-timeline-sidebar-header-cont,
.mbsc-ios.mbsc-timeline-sidebar-resource,
.mbsc-ios.mbsc-timeline-slot-header,
.mbsc-ios.mbsc-timeline-slots,
.mbsc-ios.mbsc-calendar-header,
.mbsc-ios.mbsc-calendar-wrapper {
  border-color: #f3f4f6;
}
.mbsc-timeline-row-gutter {
  height: 6px;
}
.mbsc-schedule-event-resize-start {
  display: none !important;
}
.mbsc-timeline-parent {
  font-size: 14px;
  min-height: 30px !important;
  height: 30px;

  flex-direction: row-reverse;
  padding-left: 15px !important;
  padding-right: 15px;
  .mbsc-timeline-resource-title {
    font-weight: 500;
    padding-left: 9px; //Fix Alignment of Department Titles and Drawer Title Tide-190
  }
}
.mbsc-timeline-column {
  min-height: 56px;
}
.mbsc-schedule-time-indicator-time.mbsc-schedule-time-indicator-time-x {
  background-color: #2563eb; /* A nice shade of blue */
  color: white; /* Sets the text color to white */
  padding: 2px 6px; /* Adds some vertical and horizontal spacing */
  border-radius: 4px; /* Rounds the corners of the container */
}
/*
  This targets the pseudo-element that creates the vertical line
  and centers it within its container.
*/
.mbsc-schedule-time-indicator-x::after {
  top: 50% !important;
  transform: translateY(-50%) !important;
}

/*
  This re-centers the time label directly above the
  now-centered line.
*/
.mbsc-schedule-time-indicator-time-x {
  top: 50% !important;
  transform: translate(15%, -42%) !important;
}
</style>
